import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable, SecurityContext } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { isObservable, Observable, of, timer } from 'rxjs';

import { environment } from '@env/environment';
import { catchError, map, retry, share, tap } from 'rxjs/operators';
import { resourceLoaderFactory } from '@miq/utils';
import { L10nTranslationService } from 'angular-l10n';

// The image handler can fail under load (5xx): retry before the placeholder is cached for the rest of the session
const IMAGE_LOAD_RETRIES = 2;
const IMAGE_LOAD_RETRY_DELAY_MS = 500;

@Injectable({
  providedIn: 'root',
})
export class SecuredResourceResolverService {
  private _imageCache = {};
  private _videoCache = {};
  resolverServiceWithByPassSecurity: Function;
  resolverService: Function;

  constructor(
    private http: HttpClient,
    domSanitizer: DomSanitizer,
    public translationService: L10nTranslationService
  ) {
    const sanitizeUrl = e => domSanitizer.sanitize(SecurityContext.URL, URL.createObjectURL(e));
    this.resolverServiceWithByPassSecurity = resourceLoaderFactory(this.http, sanitizeUrl);

    const simpleBlobCreator = e => URL.createObjectURL(e);
    this.resolverService = resourceLoaderFactory(this.http, simpleBlobCreator);
  }

  loadImage(uri: string, params = { responseType: 'blob' }): Observable<Object> {
    if (this._imageCache[uri]) {
      const isObservableBool = typeof this._imageCache[uri].subscribe === 'function';
      return isObservableBool ? this._imageCache[uri] : of(this._imageCache[uri]);
    }
    this._imageCache[uri] = this.resolverService(uri, params).pipe(
      retry({ count: IMAGE_LOAD_RETRIES, delay: (_, retryCount) => timer(retryCount * IMAGE_LOAD_RETRY_DELAY_MS) }),
      tap(response => (this._imageCache[uri] = response)),
      catchError(() => {
        this._imageCache[uri] = environment.appConstants.BUILDING_PH_IMAGE;
        throw new Error(this.translationService.translate('shared.couldNotRetrieveImage'));
      }),
      share()
    );
    return this._imageCache[uri];
  }

  downloadImage(uri: string) {
    return this.http.get(uri, { responseType: 'blob', observe: 'response', withCredentials: true }).pipe(
      map(({ body }: HttpResponse<any>) => ({
        blob: body,
      })),
      catchError(() => {
        throw new Error(this.translationService.translate('shared.errorWhileDownloadImage'));
      })
    );
  }

  loadSanitizedImage(uri: string, params = { responseType: 'blob' }): Observable<Object> {
    return this.resolverServiceWithByPassSecurity(uri, params);
  }

  loadSanitizedVideoUri(uri: string, params = { responseType: 'blob' }): Observable<Object> {
    if (this._videoCache[uri]) {
      return isObservable(this._videoCache[uri]) ? this._videoCache[uri] : of(this._videoCache[uri]);
    }
    this._videoCache[uri] = this.resolverServiceWithByPassSecurity(uri, params).pipe(
      tap(response => (this._videoCache[uri] = response)),
      catchError(() => {
        throw new Error(this.translationService.translate('shared.couldNotRetrieveVideo'));
      }),
      share()
    );
    return this._videoCache[uri];
  }
}
